'use client'

import SignInConfirmModal from '@/app/[locale]/[roomId]/components/SignInConfirmModal'
import IconSend from '@/components/assets/icon_send.svg'
import ViverseIconButton, { ViverseIconButtonSize } from '@/components/ViverseIconButton'
import { useAuthContext } from '@/context/AuthContext'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { ChangeEvent, useState } from 'react'
import TextareaAutosize, { TextareaAutosizeProps } from 'react-textarea-autosize'
import { useIsClient, useMediaQuery } from 'usehooks-ts'

type CommentTextareaProps = TextareaAutosizeProps & {
  textAreaElementId: string
  value: string
  disabled?: boolean
  submitButtonSize?: ViverseIconButtonSize
  containerClassName?: string
  onSubmit?: () => void
}

function CommentTextarea({
  textAreaElementId,
  value,
  disabled,
  submitButtonSize,
  className,
  containerClassName,
  onChange,
  onSubmit,
  ...props
}: CommentTextareaProps) {
  const t = useTranslations()
  const isTablet = useMediaQuery('(min-width: 768px)')
  const { isSignedIn } = useAuthContext()
  const [showSignInModal, setShowSignInModal] = useState(false)
  const isClient = useIsClient()
  const isClientSignIn = isClient && isSignedIn

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      if (onSubmit && value.trim() && !disabled) {
        onSubmit()
      }
    }
  }

  return (
    <>
      <SignInConfirmModal open={showSignInModal} onClose={() => setShowSignInModal(false)} />
      <div
        className={cn('relative flex min-h-[34px] w-full items-center gap-2', containerClassName)}
      >
        <TextareaAutosize
          id={textAreaElementId}
          value={value}
          placeholder={t('comment-field.placeholder')}
          maxRows={isTablet ? undefined : 5}
          className={cn(
            'bg-vive-primary-70 disabled:bg-vive-primary-80 disabled:placeholder:text-vive-primary-60 hover:bg-vive-primary-60 text-vive-primary-20 hover:text-vive-white focus:outline-vive-brand-50! placeholder:text-vive-primary-30 size-full resize-none rounded-[3px] px-4 py-[5px] outline-transparent focus:placeholder:text-transparent focus-visible:outline-2',
            className,
          )}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={!isClientSignIn || disabled}
          {...props}
        />
        {isClientSignIn ? (
          <div className="flex h-full flex-col justify-end">
            <ViverseIconButton
              size={submitButtonSize}
              variant="empty"
              onClick={onSubmit}
              disabled={disabled || !value.trim()}
            >
              <IconSend className="fill-vive-primary-20 group-hover:fill-vive-white group-active:fill-vive-primary-40 size-5 md:size-6" />
            </ViverseIconButton>
          </div>
        ) : (
          <span
            onClick={() => setShowSignInModal(true)}
            className="text-primary tracking=[0.15px] hover:text-vive-brand-40 active:text-vive-brand-60 absolute right-7 cursor-pointer text-xs leading-5.5 font-semibold md:text-sm"
          >
            {t('comment-textarea.sign-in')}
          </span>
        )}
      </div>
    </>
  )
}

export default CommentTextarea
